/**
 * Skills Find Command
 * Search for skills on Jai1 server and/or npm skills registry
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { ConfigService } from '../../services/config.service.js';
import { SkillsService } from '../../services/skills.service.js';
import { ValidationError } from '../../errors/index.js';

export function createSkillsFindCommand(): Command {
    return new Command('find')
        .description('Search for skills on server or npm')
        .argument('<query>', 'Search query')
        .option('--skillsh', 'Search on npm skills registry instead of Jai1 server')
        .option('--all', 'Search on both Jai1 server and npm')
        .option('--json', 'Output machine-readable JSON')
        .option('--limit <number>', 'Maximum results per source (1-10)', '10')
        .option('--refresh', 'Bypass cached provider data')
        .action(async (query: string, options: {
            skillsh?: boolean;
            all?: boolean;
            json?: boolean;
            limit?: string;
            refresh?: boolean;
        }, command: Command) => {
            const searchNpm = options.skillsh || options.all;
            const searchServer = !options.skillsh || options.all;
            const rootOptions = command.parent?.parent?.opts<{ json?: boolean }>();
            const jsonMode = options.json === true || rootOptions?.json === true;
            const parsedLimit = Number.parseInt(options.limit ?? '10', 10);
            if (!Number.isInteger(parsedLimit) || parsedLimit < 1 || parsedLimit > 10) {
                throw new ValidationError('--limit phải là số nguyên từ 1 đến 10.');
            }

            if (jsonMode) {
                const skillsService = new SkillsService();
                const candidates: Array<Record<string, unknown>> = [];
                const sources: Array<{ source: 'jai1' | 'skills.sh'; status: 'completed' | 'degraded'; count: number }> = [];
                const errors: Array<{ source: 'jai1' | 'skills.sh'; message: string }> = [];

                if (searchServer) {
                    try {
                        const config = await new ConfigService().load();
                        if (!config) throw new Error('Chưa xác thực. Chạy "jai1 auth" trước.');
                        const results = (await skillsService.searchFromServer(config, query)).slice(0, parsedLimit);
                        candidates.push(...results.map((skill) => {
                            const slug = skill.filepath.replace(/^skills\//, '');
                            return {
                                id: `jai1:${slug}`,
                                source: 'jai1',
                                sourceRef: slug,
                                name: skill.name || slug,
                                description: skill.description || '',
                                popularity: skill.downloads || 0,
                                version: skill.version,
                                tags: skill.tags || [],
                            };
                        }));
                        sources.push({ source: 'jai1', status: 'completed', count: results.length });
                    } catch (error) {
                        sources.push({ source: 'jai1', status: 'degraded', count: 0 });
                        errors.push({ source: 'jai1', message: error instanceof Error ? error.message : String(error) });
                    }
                }

                if (searchNpm) {
                    try {
                        const results = await skillsService.searchSkillsSh(query, {
                            limit: parsedLimit,
                            refresh: options.refresh,
                        });
                        candidates.push(...results);
                        sources.push({ source: 'skills.sh', status: 'completed', count: results.length });
                    } catch (error) {
                        sources.push({ source: 'skills.sh', status: 'degraded', count: 0 });
                        errors.push({ source: 'skills.sh', message: error instanceof Error ? error.message : String(error) });
                    }
                }

                console.log(JSON.stringify({
                    schemaVersion: 1,
                    query,
                    limitPerSource: parsedLimit,
                    sources,
                    candidates,
                    errors,
                }, null, 2));
                if (sources.length > 0 && sources.every((source) => source.status === 'degraded')) {
                    process.exitCode = 1;
                }
                return;
            }

            // Search Jai1 server
            if (searchServer) {
                const configService = new ConfigService();
                const config = await configService.load();

                if (!config) {
                    throw new ValidationError('Chưa xác thực. Chạy "jai1 auth" trước.');
                }

                console.log(chalk.cyan('🔍 Đang tìm kiếm trên Jai1 server...'));
                console.log();

                const skillsService = new SkillsService();
                const results = await skillsService.searchFromServer(config, query);

                if (results.length === 0) {
                    console.log(chalk.yellow('Không tìm thấy skills nào trên server.'));
                } else {
                    console.log(chalk.bold(`📦 Jai1 Server (${results.length} kết quả)\n`));

                    for (const skill of results) {
                        const name = skill.filepath.replace('skills/', '');
                        const version = skill.version ? chalk.green(`v${skill.version}`) : chalk.dim('-');
                        const downloads = chalk.dim(`${skill.downloads || 0} downloads`);
                        const desc = skill.description || '';
                        const maxDesc = 120;
                        const truncatedDesc = desc.length > maxDesc ? desc.slice(0, maxDesc) + '…' : desc;

                        console.log(`  ${chalk.bold.white(name)}  ${version} · ${downloads}`);
                        if (truncatedDesc) {
                            console.log(`  ${chalk.dim(truncatedDesc)}`);
                        }
                        console.log();
                    }
                }
            }

            // Search npm skills registry
            if (searchNpm) {
                console.log(chalk.cyan('🔍 Đang tìm kiếm trên npm skills...'));
                console.log();

                const skillsService = new SkillsService();
                try {
                    const output = await skillsService.npmSkillsFind(query);
                    console.log(chalk.bold('🌐 npm Skills Registry'));
                    console.log(output);
                } catch (error) {
                    console.log(chalk.yellow(
                        `Không thể tìm kiếm trên npm: ${error instanceof Error ? error.message : String(error)}`
                    ));
                }
            }

            // Hint - context-aware based on search source
            if (searchServer && !searchNpm) {
                console.log(chalk.dim('💡 Dùng "j skills add <tên>" để cài đặt từ server'));
            } else if (searchNpm && !searchServer) {
                console.log(chalk.dim('💡 Dùng "j skills add <owner/repo@skill> --skillsh" để cài đặt'));
            } else {
                console.log(chalk.dim('💡 Cài đặt:'));
                console.log(chalk.dim('   Server:   j skills add <tên>'));
                console.log(chalk.dim('   Skills.sh: j skills add <owner/repo@skill> --skillsh'));
            }
        });
}
